import { Color } from 'three'
import { BoxTypeEnum, ColorType } from '../core/enums'
import type { ManifestMaterial, ManifestRGBA } from './assetLibrary'

/**
 * How the shipped game colours a bus.
 *
 * `Bus.SwitchColor` does not tint anything. It swaps the renderer's material
 * for the one `ColorTypeExtensions.GetCarMaterial(color)` returns — a lookup
 * into a palette list keyed by `ColorType`, with a matching
 * `GetAccommodationMaterial` for the seats. Every one of those materials is
 * white with the same `BusTex`; what differs is three colours the
 * `Custom_StylizedToon` shader blends against the mesh's vertex-colour mask
 * (`_VertexColorRColor` / `G` / `B`).
 *
 * So the port needs both halves: pick the right material by `ColorType`, then
 * resolve the mask into real per-vertex colours. Multiplying a base colour —
 * what this used to do — cannot reproduce it, because the base colour is white
 * in every variant.
 */

/** `ColorType` -> material suffix. The assets spell grey the American way. */
const COLOR_NAME: Readonly<Partial<Record<ColorType, string>>> = {
  [ColorType.Blue]: 'Blue',
  [ColorType.Green]: 'Green',
  [ColorType.Orange]: 'Orange',
  [ColorType.Purple]: 'Purple',
  [ColorType.Red]: 'Red',
  [ColorType.Turquoise]: 'Turquoise',
  [ColorType.Yellow]: 'Yellow',
  [ColorType.White]: 'White',
  [ColorType.Grey]: 'Gray',
  [ColorType.Brown]: 'Brown',
  [ColorType.Pink]: 'Pink',
  // `Black` and `None` have no material of their own.
  //
  // ⚠️ `None` is what `Bus.visibleColor` reports for a Hidden bus, and mapping it
  // to grey is **wrong** — this comment used to claim "grey is what the original
  // shows for one", and that claim is what keeps the bug alive.
  //
  // A hidden bus really is drawn with Bus_Gray, and ColorType.None is how this
  // port spells it.
  //
  // SwitchColor__0x20f8938.c:58-65 swaps the material to
  // GetCarMaterial(ColorType.Grey) — 9, dump.cs:709187 — in the same branch
  // that then calls ApplyHiddenMaterialState five lines down. The grey is the
  // original's own choice, not a stand-in.
  //
  // The note that used to sit here said ApplyHiddenMaterialState never touches
  // which material is on the renderer, and works on the bus's own colour
  // material — Bus_Red and so on — and concluded the grey was wrong. It does
  // write materials[0], but by then materials[0] IS Grey's. What the port was
  // missing is the second half: the dissolve state on top of the grey. See
  // hiddenMaterialSpec below, and todo.md for the texture that finishes it.
  [ColorType.Black]: 'Gray',
  [ColorType.None]: 'Gray',
}

/** Material name for a bus body of this colour, e.g. `Bus_Red`. */
export function carMaterialName(color: ColorType): string {
  return `Bus_${COLOR_NAME[color] ?? 'Gray'}`
}

/** Material name for the seats, e.g. `BusAcc_Red`. */
export function accommodationMaterialName(color: ColorType): string {
  return `BusAcc_${COLOR_NAME[color] ?? 'Gray'}`
}

/**
 * 📐 `Bus.iceMaterial` (`dump.cs:709419`, field 0x78) is `Material/IceBus.mat`.
 *
 * A stripped `[SerializeField]`, so no prefab says so; `tests/iceMaterial.test.ts`
 * is the elimination — `Ice 1.mat` is the `ice_vfx` particle's, `IceBus.mat` is
 * the one orphan with an ice parameter set. What the manifest reads off it
 * (`buildAssets.mjs`, `EXTRA_MATERIALS`): `_BaseColor (0.797, 0.963, 1)` sRGB,
 * `_BaseMap BusTex_Ice`, `_RampThreshold 0.244`, `_ShadowColor (0.553, 0.847,
 * 0.953)`, `_ShadowColorLift 0.335`, `_AmbientBoost 1.599`, `_ReceiveShadows 1`,
 * `_Surface 0` (opaque), `_UseVertexColor 0` — no palette, so the bus's own
 * colour is nowhere on an iced body.
 *
 * ❓ PLACEHOLDER: `_TextureVisibility 0.7` is a property of the same shader that
 * no `Bus_*.mat` stores and `ToonMaterial` does not read — the HLSL is a
 * `DummyShaderTextExporter` stub, so what 0.7 does to the base map is not
 * recoverable. The port samples the map at full strength. §10 of the checklist.
 */
export const ICE_MATERIAL_NAME = 'IceBus'

/**
 * 🔍 Which material a bus body and its rings are drawn with, as the tail of
 * `Bus.SwitchColor` (`__0x20f8938.c`) leaves them:
 *
 *     ApplyMaterialToAll(GetCarMaterial(color));            // meshRenderers 0x20 + 3 BusBodyMesh
 *     foreach (r in accommodationObjects) r.sharedMaterial = GetAccommodationMaterial(color);  // :212
 *     if (normal && _type == Ice) ApplyIceMaterialState();  // :241
 *
 * and `ApplyIceMaterialState` (`__0x20f9eac.c`:84/:138/:192/:235/:284/:327)
 * writes `iceMaterial` into **element [0]** of `sharedMaterials` — `plVar3[4]`
 * is the first element of an IL2CPP array, after `max_length` at `[3]` — on
 * every renderer in `meshRenderers` (0x20), `accommodationObjects` (0x68) and
 * the three `BusBodyMesh` (0xA8/0xB0/0xB8, renderer at +0x60). No extra slot,
 * no second mesh: the colour material is replaced, and `Bus.BreakIce`
 * (`__0x20fb820.c`:37/:65 `_type = 0`, then the inlined `SwitchColor`) puts it
 * back by running the same tail with `_type` no longer `Ice`.
 *
 * The hidden branch (`normal == false`) never reaches the ice line, which is
 * why `Hidden` is not consulted here: `BusFleet.build` wraps the grey in the
 * dissolve twin afterwards, and a bus has one `_type`.
 */
export function busBodyMaterials(
  color: ColorType,
  type: BoxTypeEnum,
): { body: string; seats: string } {
  if (type === BoxTypeEnum.Ice) return { body: ICE_MATERIAL_NAME, seats: ICE_MATERIAL_NAME }
  return { body: carMaterialName(color), seats: accommodationMaterialName(color) }
}

/**
 * Material name for a passenger, e.g. `Char_Red`.
 *
 * `Character.SwitchColor` is the same trick as `Bus.SwitchColor` — the
 * `Stickman` prefab ships with `Char_Pink` on it and the colour is swapped at
 * spawn. All twelve are white over `CharTex` with their own
 * `_VertexColorRColor` / `G` / `B`, so the mesh's mask has to be resolved the
 * same way a bus body's is.
 */
export function characterMaterialName(color: ColorType): string {
  return `Char_${COLOR_NAME[color] ?? 'Gray'}`
}

/** Every colour a passenger can be drawn in. */
export const PALETTE_COLORS: readonly ColorType[] = Object.keys(COLOR_NAME).map(
  (k) => Number(k) as ColorType,
)

const _c = new Color()
const _t = new Color()

/**
 * Blend one vertex-colour mask sample against a material's palette.
 *
 * The mask is not a colour: a body vertex carries (1,0,0), (0,1,0) or (1,1,1)
 * to choose a shade, and (0,0,0) to keep the base texture untouched. The shader
 * blends the three palette entries in channel order, so a later channel wins
 * where masks overlap — which is why (1,1,1) reads as the B shade.
 *
 * The shipped game does this on the GPU and so does the port; `toonMaterial.ts`
 * has the same three lines in GLSL. This copy exists for the offline
 * rasteriser and the colour checks, which have no shader to run — keep the two
 * in step.
 *
 * Returns a shared `Color`; copy it if you need to keep it.
 */
export function blendVertexPalette(
  mr: number,
  mg: number,
  mb: number,
  palette: NonNullable<ManifestMaterial['palette']>,
): Color {
  let cr = 1
  let cg = 1
  let cb = 1
  for (const [entry, weight] of [
    [palette.r, mr],
    [palette.g, mg],
    [palette.b, mb],
  ] as const) {
    if (!entry) continue
    cr += (entry.r - cr) * weight
    cg += (entry.g - cg) * weight
    cb += (entry.b - cb) * weight
  }
  // Authored in sRGB, like every other colour in a `.mat`.
  return _c.setRGB(cr, cg, cb, 'srgb')
}

/**
 * 🔍 `_DissolveHiddenColor` — what a hidden bus's surface is replaced with.
 *
 * Every one of the 22 palette materials (`Bus_*.mat` + `BusAcc_*.mat`) carries
 * the **identical** value; the only two files without the property are
 * `Bus_0.mat` and `Bus_1.mat`, the older-shader materials this port never
 * draws. sRGB, like every colour in a `.mat` (CLAUDE.md §8).
 */
export const DISSOLVE_HIDDEN_COLOR = { r: 0.29874, g: 0.32093838, b: 0.39, a: 1 }

/** 🔍 `SetColor(ShadowColor, new Color(0, 0, 0, 1))` — `ApplyHiddenMaterialState`. */
export const HIDDEN_SHADOW_COLOR = { r: 0, g: 0, b: 0, a: 1 }

/**
 * 🔍 `Bus.ApplyHiddenMaterialState` (RVA 0x20F9AC4), as a material spec.
 *
 * The original does **not** swap in a grey material. It takes `materials[0]` of
 * every `meshRenderers[]` (0x20) and `accommodationObjects` (0x68) — the bus's
 * real colour — and writes two properties:
 *
 *     mat.SetFloat(DissolveRange, 0f);                    // Bus statics +8
 *     mat.SetColor(ShadowColor, new Color(0, 0, 0, 1));    // Bus statics +4
 *
 * (`dump.cs:709485-709489` names the three static ids; `Bus$$.cctor` writes the
 * `PropertyToID` results to +0/+4/+8 in that order.)
 *
 * Two properties and nothing else — so the base colour and the vertex palette
 * are left exactly as they were. What hides the bus is the shader reacting to
 * `_DissolveRange = 0`; see `dissolveMix`.
 *
 * The direction is settled by `PlayRevealMaterialTween__0x20f94ec.c`:98-112,
 * which sets the range to 0 and then `DOFloat`s it to `1.0` over `0x3f649ba6` =
 * 0.893 s: **0 is hidden, 1 is normal**, and the authored value in every
 * shipped `.mat` is 1.
 */
export function hiddenMaterialSpec(spec: ManifestMaterial): ManifestMaterial {
  return {
    ...spec,
    // `SetFloat(DissolveRange, 0f)`.
    dissolveRange: 0,
    // `SetColor(ShadowColor, new Color(0, 0, 0, 1))`.
    toon: { ...spec.toon, shadow: { ...HIDDEN_SHADOW_COLOR } },
  }
}

/**
 * 🔍 `Bus.PlayRevealMaterialTween` (RVA 0x20F94EC): `0x3f649ba6` = 0.893 s, the
 * duration of both the `DOFloat` (`:107`, `:319` of the inlined copy in
 * `ExitHiddenState`) and the `DOColor` (`:113`, `:330`). Decoded from the hex
 * in `busCoverage.test.ts`, not typed in.
 */
export const REVEAL_TWEEN_SECONDS = 0.893

/**
 * 🔍 `Bus.PlayRevealMaterialTween`, as the material's two properties at
 * `elapsed` seconds into the sequence.
 *
 * `ExitHiddenState__0x20f91bc.c` inlines the whole function, and the order is
 * the point (HID-5): `:135` `SwitchColor(_color, 0)` puts the **real** colour's
 * material back on every renderer first, then `:148` `Play(0xb)`, then per
 * `materials[0]` of `meshRenderers` (`:207-232`) and `accommodationObjects`
 * (`:313-340`):
 *
 *     var target = mat.GetColor(ShadowColor);          // the real colour's _ShadowColor
 *     mat.SetFloat(DissolveRange, 0f);
 *     mat.SetColor(ShadowColor, new Color(0, 0, 0, 1));
 *     _hiddenSequence.Insert(0, mat.DOFloat(1f, DissolveRange, 0.893f));
 *     _hiddenSequence.Insert(?, mat.DOColor(target, ShadowColor, 0.893f));
 *
 * So `target` is `Bus_<Colour>._ShadowColor`, **not** `Bus_Gray`'s 0.588: the
 * `SwitchColor:58` branch that picks Grey needs `_type == Hidden`, and
 * `DeactivateHiddenState:84` / `SetFeature:26` have already written `_type = 0`
 * before `ExitHiddenState` is reached. Two consequences worth naming: on
 * `Bus_Red` / `Bus_Pink` the colour half is black → black and only the dissolve
 * shows; on `BusAcc_Red` / `BusAcc_Pink` it is not (`BusAcc_Red.mat:204` is
 * 0.78/0.19/0.19), so the seat rings still shade in. The `Insert` time of the
 * `DOColor` is dropped by Ghidra (`:231` writes `param_1 = 0` just before, which
 * reads as 0 but is not a direct read); with both tweens the same length,
 * anything but 0 would leave the shadow black after the dissolve had finished.
 *
 * The colour argument of `ExitHiddenState` is dropped at both gameplay call
 * sites too (`DeactivateHiddenState:121`, `SetFeature:131`); `SwitchColor(_color,
 * 0)` is inferred from the only colour field the class has (0x120) and from the
 * sibling branch `SetFeature:127-128`, which passes exactly that.
 *
 * Both curves are `DOFloat` / `DOColor` on the authored values, so the shadow
 * interpolates in the `.mat`'s own sRGB components, as DOTween's `Color` lerp
 * does; convert with `toonColor` **after** mixing, never before.
 *
 * ❓ PLACEHOLDER — ease. Neither tween calls `SetEase`, so DOTween's
 * `defaultEaseType` applies, and `Resources/DOTweenSettings.asset` exported with
 * every serialised field stripped, so the project's default is unrecoverable
 * (the library's own default is `OutQuad`). Linear here, like the ring wave and
 * the other stripped-ease tweens; §10 of the checklist carries the row.
 */
export function revealMaterialAt(
  spec: ManifestMaterial,
  elapsed: number,
): { dissolveRange: number; shadow: ManifestRGBA } {
  const k = Math.min(Math.max(elapsed / REVEAL_TWEEN_SECONDS, 0), 1)
  const from = HIDDEN_SHADOW_COLOR
  const to = spec.toon.shadow
  return {
    dissolveRange: k,
    shadow: {
      r: from.r + (to.r - from.r) * k,
      g: from.g + (to.g - from.g) * k,
      b: from.b + (to.b - from.b) * k,
      a: from.a + (to.a - from.a) * k,
    },
  }
}

/**
 * What `_DissolveRange` does to a surface — the same three lines the port's
 * GLSL runs, for the offline rasteriser and the colour checks, which have no
 * shader. Keep the two in step, exactly as with `blendVertexPalette`.
 *
 * 🔍 The ingredients are read from the shipped assets:
 *
 * - `Custom_StylizedToon.shader`:26-37 survived AssetRipper as a Properties
 *   block (only the HLSL became a `DummyShaderTextExporter` stub). It names
 *   `_DissolveTexture` **"Hidden Color Texture"** and `_DissolveTextureAlpha`
 *   **"Alpha"**, gated `[ENABLETEXTURE:_DissolveTexture]`. So the texture is an
 *   alternative to the flat `_DissolveHiddenColor`, faded in by that slider.
 * - The dissolve texture has no triplanar toggle, unlike `_MaskTexture`
 *   (`[Enum(UV,0,Triplanar,1)] _MaskDetailType`) and the three
 *   `_UseVertexColor?Triplanar` toggles. Plain UV0 with `_DissolveTexture_ST`
 *   is the only sampling this shader offers.
 * - Every one of the 22 palette materials sets `_UseDissolve: 1`,
 *   `_DissolveTextureAssigned: 1`, `_DissolveTexture: hidden` tiled 0.225, and
 *   `_DissolveTextureAlpha: 0.38`.
 *
 * 📐 What the texture holds is measured, not guessed: `hidden.png` is 256x256
 * RGBA whose **alpha is the glyph mask** — 79.8% of texels are alpha 0, 17.5%
 * alpha 255 — and where alpha > 200 the mean RGB is 199.7, i.e. white question
 * marks with a thin dark outline over a transparent field. Tiled 0.225, one
 * glyph covers a bus panel. `web/tests/hiddenBus.test.ts` reads the file and
 * locks both halves.
 *
 * ❓ PLACEHOLDER on the exact composite. The property names say "hidden colour,
 * plus a texture at this alpha", and this reads that the only way that uses
 * both the mask and the slider:
 *
 *     wrapped = lerp(hiddenColor, texel.rgb, texel.a * textureAlpha)
 *     out     = lerp(wrapped, albedo, range)
 *
 * Unresolved: whether the shader multiplies the two alphas or uses the slider
 * alone, and whether `_DissolveNoiseScale` / `_DissolveEdge*` shape anything at
 * range 0 (they should not: at 0 the whole surface is past the edge). To settle
 * it: the real HLSL, which this build does not ship.
 *
 * `hidden` is sRGB, like every colour in a `.mat` (CLAUDE.md §8), and so is the
 * texel; `albedo` and the result are linear, like the rest of the rasteriser.
 */
export function dissolveMix(
  albedo: readonly [number, number, number],
  hidden: { r: number; g: number; b: number },
  texel: { r: number; g: number; b: number; a: number } | null,
  textureAlpha: number,
  range: number,
): [number, number, number] {
  if (range >= 1) return [albedo[0], albedo[1], albedo[2]]
  const h = _c.setRGB(hidden.r, hidden.g, hidden.b, 'srgb')
  let wr = h.r
  let wg = h.g
  let wb = h.b
  if (texel) {
    const stamp = _t.setRGB(texel.r, texel.g, texel.b, 'srgb')
    const k = texel.a * textureAlpha
    wr += (stamp.r - wr) * k
    wg += (stamp.g - wg) * k
    wb += (stamp.b - wb) * k
  }
  return [
    wr + (albedo[0] - wr) * range,
    wg + (albedo[1] - wg) * range,
    wb + (albedo[2] - wb) * range,
  ]
}
